import { useEffect, useState } from "react";
import { Share2, Copy, CheckCircle2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { trackEvent } from "@/lib/ga4";

/**
 * Inline share block. Three default messages rotate randomly per
 * **page-view** so each visitor sees one variant. Choice is persisted
 * for ~24h via sessionStorage to avoid flicker if the user opens
 * multiple pages in the same browsing session. We track which variant
 * was shown and which channel was clicked so the editor can compare
 * conversion in GA4 (`about_share_view` + `about_share_click`).
 */
const SITE_URL = "https://juristube.com.br";

const SHARE_VARIANTS = [
  {
    id: "A_descritiva",
    text: "Conheça o JurisTube — um acervo aberto e gratuito de vídeos, artigos, podcasts, aulas, notícias e legislação, focado em Direito Público, organizado por autores, temas e coleções. Uma plataforma essencial para quem estuda, leciona ou advoga na área!",
  },
  {
    id: "B_features",
    text: "Estou usando o JurisTube: um ecossistema gratuito de Direito Público que reúne vídeos, artigos, podcasts, notícias e legislação com gerador automático de referências (ABNT/APA), acessibilidade por voz e PDFs acadêmicos. Vale muito a pena conferir!",
  },
  {
    id: "C_concisa",
    text: "Vídeos, artigos, podcasts e legislação federal — tudo no JurisTube. Acervo aberto de Direito Público. Vale muito a pena conferir!",
  },
];

const VARIANT_STORAGE_KEY = "juristube:about-share:variant";
const VARIANT_TTL_MS = 24 * 60 * 60 * 1000; // 24h

/**
 * Sorteia uma variante ponderada IGUALMENTE (a/b/c = 33%/33%/33%) e
 * fixa a escolha por 24h via sessionStorage para o A/B/C test ser
 * "sticky" dentro da sessão — evita um visitante ver duas variantes
 * diferentes ao navegar entre páginas, o que enviesaria os clicks.
 */
function pickStickyVariant() {
  try {
    const stored = sessionStorage.getItem(VARIANT_STORAGE_KEY);
    if (stored) {
      const { id, ts } = JSON.parse(stored);
      if (id && ts && Date.now() - ts < VARIANT_TTL_MS) {
        const found = SHARE_VARIANTS.find((v) => v.id === id);
        if (found) return found;
      }
    }
  } catch {
    // sessionStorage indisponível (private browsing) — segue o jogo,
    // só não persistirá.
  }
  const idx = Math.floor(Math.random() * SHARE_VARIANTS.length);
  const chosen = SHARE_VARIANTS[idx];
  try {
    sessionStorage.setItem(VARIANT_STORAGE_KEY, JSON.stringify({ id: chosen.id, ts: Date.now() }));
  } catch {}
  return chosen;
}

export default function AboutShareBlock({ compact = false }) {
  const [variant, setVariant] = useState(() => pickStickyVariant());
  const [msg, setMsg] = useState(variant.text);
  const [copied, setCopied] = useState(false);
  const DEFAULT_MSG = variant.text;

  // Track impression — 1 evento por mount. Permite ao editor saber
  // qual variante foi vista e em qual página (page_path vem do GA4
  // automaticamente).
  useEffect(() => {
    trackEvent("about_share_view", { variant: variant.id });
  }, [variant.id]);

  const cleanMsg = (msg || "").trim();
  const text = cleanMsg ? `${cleanMsg}\n\n${SITE_URL}` : SITE_URL;
  const encoded = encodeURIComponent(text);
  const encodedUrl = encodeURIComponent(SITE_URL);
  const encodedMsgOnly = encodeURIComponent(cleanMsg || DEFAULT_MSG);
  const wasCustomized = cleanMsg && cleanMsg !== DEFAULT_MSG;

  const SHARES = [
    {
      id: "whatsapp",
      label: "WhatsApp",
      brand: "bg-[#25D366]/20 hover:bg-[#25D366]/30 text-[#25D366] border-[#25D366]/40",
      href: `https://wa.me/?text=${encoded}`,
    },
    {
      id: "linkedin",
      label: "LinkedIn",
      brand: "bg-[#0A66C2]/20 hover:bg-[#0A66C2]/30 text-[#4c9beb] border-[#0A66C2]/40",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    },
    {
      id: "x",
      label: "X (Twitter)",
      brand: "bg-slate-600/20 hover:bg-slate-500/30 text-slate-100 border-slate-500/40",
      href: `https://twitter.com/intent/tweet?text=${encodedMsgOnly}&url=${encodedUrl}`,
    },
    {
      id: "facebook",
      label: "Facebook",
      brand: "bg-[#1877F2]/20 hover:bg-[#1877F2]/30 text-[#3b82f6] border-[#1877F2]/40",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedMsgOnly}`,
    },
    {
      id: "telegram",
      label: "Telegram",
      brand: "bg-[#229ED9]/20 hover:bg-[#229ED9]/30 text-[#60b8e4] border-[#229ED9]/40",
      href: `https://t.me/share/url?url=${encodedUrl}&text=${encodedMsgOnly}`,
    },
  ];

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      trackEvent("about_share_click", {
        variant: variant.id,
        channel: "copy",
        customized: wasCustomized,
      });
      toast.success("Texto copiado!");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Não foi possível copiar — copie manualmente abaixo.");
    }
  };

  // Permite ao editor (ou usuário curioso) saltar para a próxima
  // variante manualmente. Atualiza o state local + sessionStorage para
  // que o próximo render renderize a nova mensagem corretamente.
  const cycleVariant = () => {
    const currentIdx = SHARE_VARIANTS.findIndex((v) => v.id === variant.id);
    const next = SHARE_VARIANTS[(currentIdx + 1) % SHARE_VARIANTS.length];
    try {
      sessionStorage.setItem(VARIANT_STORAGE_KEY, JSON.stringify({ id: next.id, ts: Date.now() }));
    } catch {}
    setVariant(next);
    setMsg(next.text);
  };

  return (
    <section
      className={`rounded-2xl border border-sky-500/30 bg-gradient-to-br from-sky-500/[0.06] via-slate-950 to-slate-950 ${compact ? "p-6 md:p-7 space-y-4" : "p-8 md:p-10 space-y-5"}`}
      data-testid="about-share-block"
    >
      <div className="flex items-start gap-3">
        <div className="mt-1 p-2 rounded-lg bg-sky-500/10 border border-sky-500/30 shrink-0">
          <Share2 className="w-5 h-5 text-sky-400" />
        </div>
        <div className="space-y-2">
          <h2 className={`font-serif font-bold text-white ${compact ? "text-xl md:text-2xl" : "text-2xl md:text-3xl"}`}>
            {compact ? "Gostou do JurisTube? Compartilhe com colegas" : "Divulgue o JurisTube nas suas redes"}
          </h2>
          <p className={`text-slate-300 leading-relaxed max-w-2xl ${compact ? "text-xs md:text-sm" : "text-sm md:text-base"}`}>
            {compact
              ? "Um acervo aberto cresce quando cada pessoa compartilha. Escreva um comentário seu (opcional) — ele será incluído junto com o link."
              : "O JurisTube cresce quando cada pessoa da comunidade compartilha. Se quiser, escreva abaixo um comentário pessoal sobre o serviço — ele será adicionado ao link automaticamente."}
          </p>
        </div>
      </div>

      <div>
        <label htmlFor="share-msg" className="text-xs uppercase tracking-wider text-slate-400 font-semibold block mb-1.5">
          Sua mensagem (opcional)
        </label>
        <textarea
          id="share-msg"
          value={msg}
          onChange={(e) => setMsg(e.target.value.slice(0, 500))}
          rows={4}
          placeholder="Ex.: Acabei de descobrir esse acervo sobre Direito Público — vale muito para quem leciona!"
          className="w-full bg-slate-900/80 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-sky-500/60 resize-none"
          data-testid="about-share-message-input"
        />
        <div className="flex items-center justify-between mt-1">
          <span className="text-[11px] text-slate-500">{(msg || "").length}/500 caracteres</span>
          {msg !== DEFAULT_MSG && (
            <button
              type="button"
              onClick={() => setMsg(DEFAULT_MSG)}
              className="text-[11px] text-slate-400 hover:text-sky-300 underline underline-offset-2"
              data-testid="about-share-restore-default"
            >
              Restaurar mensagem padrão
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="about-share-buttons">
        {SHARES.map((s) => (
          <a
            key={s.id}
            href={s.href}
            target="_blank"
            rel="noreferrer noopener"
            onClick={() => trackEvent("about_share_click", {
              variant: variant.id,
              channel: s.id,
              customized: wasCustomized,
            })}
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full border text-sm font-medium transition ${s.brand}`}
            data-testid={`about-share-${s.id}`}
          >
            {s.label}
          </a>
        ))}
        <button
          type="button"
          onClick={copyToClipboard}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-slate-700 bg-slate-900/60 text-slate-200 hover:border-white hover:bg-slate-800 text-sm font-medium transition"
          data-testid="about-share-copy"
        >
          {copied ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
          {copied ? "Copiado!" : "Copiar texto"}
        </button>
        <button
          type="button"
          onClick={cycleVariant}
          className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-full border border-slate-800 text-slate-500 hover:text-slate-200 hover:border-slate-600 text-xs transition ml-auto"
          data-testid="about-share-cycle-variant"
          title={`Trocar mensagem de divulgação. Variante atual: ${variant.id}`}
        >
          <RotateCcw className="w-3 h-3" /> Outra mensagem
        </button>
      </div>
    </section>
  );
}
